import { useEffect, useMemo } from "react";
import {
  Col,
  DatePicker,
  Divider,
  Empty,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
  Space,
  Switch,
  TimePicker,
  Typography,
} from "antd";
import { type FormInstance } from "antd/lib/form";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import keyBy from "lodash.keyby";
import uniq from "lodash.uniq";
import { useTranslation } from "react-i18next";

import { RequestStatus, surveysTemplatesActions } from "@fitness-app/app-store";
import { CycleSurveyType, SurveyIntervalUnit, SurveyType } from "@fitness-app/data-models/entities/ClientSurvey";
import {
  ProgramAutomationScheduleAnchor,
  type WeekdayNumberToString,
} from "@fitness-app/data-models/entities/ProgramAutomation";
import {
  Weekday,
  type SurveyTemplate,
  type SurveyTemplateWithCreator,
} from "@fitness-app/data-models/entities/SurveyTemplate";
import { createScheduleOptions } from "@fitness-app/utils/src/surveys/createInitialSurvey";

import {
  createCycleTypeOptions,
  createIntervalOptions,
  createSurveyTypeOptions,
} from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeSurveys/AddSurveyForm/survey.data";
import { useAppDispatch, useAppSelector } from "~/store/initializeStore";
import { FiltersField, type FiltersFieldProps } from "../FiltersField/FiltersField";
import { ScheduleTimingFields } from "../ScheduleTimingFields/ScheduleTimingFields";
import { TimeField } from "../TimeField/TimeField";
import { type AddSurveyTemplateFormModel } from "./types";

dayjs.extend(utc);

interface AddSurveyFormProps {
  formController?: FormInstance<AddSurveyTemplateFormModel>;
  onSubmit: (formData: AddSurveyTemplateFormModel, template: SurveyTemplate) => void;
  model?: Partial<AddSurveyTemplateFormModel> | null;
  selectedCell: {
    week: string;
    day: WeekdayNumberToString;
  } | null;
  fromAutomation?: boolean;
}

const createSurveysOptions = (collection: SurveyTemplateWithCreator[]) =>
  collection.map((value) => ({
    label: value.title,
    value: value.id,
  }));
export const DISABLED_HOURS = [0, 1, 2, 3, 4, 5, 6, 23];

const AddSurveyTemplateForm = ({
  model,
  onSubmit,
  formController,
  selectedCell,
  fromAutomation,
}: AddSurveyFormProps) => {
  const { t } = useTranslation(["surveys", "common"]);
  const dispatch = useAppDispatch();
  const { list, listStatus } = useAppSelector((store) => store.surveysTemplates);
  const templatedId = Form.useWatch("templateId", formController);
  const surveyType = Form.useWatch("type", formController) as SurveyType;
  const cycleType: CycleSurveyType | undefined = Form.useWatch("cycleType", formController);

  const listById = useMemo(() => keyBy(list, "id"), [list]);

  const variables = useMemo(() => {
    const automation = listById[templatedId]?.automation;

    if (!templatedId || !automation) {
      return null;
    }

    return uniq(
      Object.values(automation)
        .map((automations) => automations.map((automation) => automation.tasks.map((task) => task.variables)).flat(2))
        .flat(2),
    )
      .filter((variable): variable is string => typeof variable === "string")
      .sort();
  }, [templatedId, listById]);

  useEffect(() => {
    void dispatch(surveysTemplatesActions.fetchSurveysTemplates());
  }, [dispatch]);

  useEffect(() => {
    if (model) {
      formController?.setFieldsValue(model);
    }
  }, [model]);

  useEffect(() => {
    if (selectedCell?.day) {
      const options = createScheduleOptions(t);
      formController?.setFieldsValue({ dayOfDelivery: options[Number(selectedCell.day) - 1]?.value || Weekday.MONDAY });
    }
  }, [selectedCell, t]);

  useEffect(() => {
    if (cycleType === CycleSurveyType.CycleNumber) {
      formController?.setFieldsValue({ cycleTypeNumber: 2 });
    }
    if (cycleType === CycleSurveyType.UntilDate) {
      formController?.setFieldsValue({ untilDate: dayjs().add(1, "week") });
    }
  }, [cycleType]);

  useEffect(() => {
    if (surveyType === SurveyType.Recurring) {
      formController?.setFieldsValue({ cycleType: CycleSurveyType.Infinite });
    }
  }, [surveyType]);

  useEffect(() => {
    if (templatedId) {
      const foundTemplate = list.find((template) => template.id === templatedId);

      if (foundTemplate) {
        formController?.setFieldsValue({ title: foundTemplate.title, description: foundTemplate.description || "" });
      }
    }
  }, [templatedId]);

  const renderCycleTypeField = () => {
    if (cycleType === CycleSurveyType.Infinite) {
      return null;
    }
    if (cycleType === CycleSurveyType.CycleNumber) {
      return (
        <Form.Item label={t("form.cycleTypeNumber")} name="cycleTypeNumber">
          <InputNumber min={2} max={100} />
        </Form.Item>
      );
    }
    if (cycleType === CycleSurveyType.UntilDate) {
      return (
        <Form.Item name="untilDate" label={t("form.untilDate")}>
          <DatePicker format="DD.MM.YYYY" disabledDate={(current) => current.isBefore(dayjs().add(1, "week"), "day")} />
        </Form.Item>
      );
    }
  };

  const handleSubmit = (formData: AddSurveyTemplateFormModel) => {
    const foundTemplate = templatedId ? list.find((template) => template.id === templatedId) : null;

    if (foundTemplate) {
      onSubmit(formData, foundTemplate);
    }
  };

  return (
    <Form<AddSurveyTemplateFormModel>
      name="form"
      labelCol={{ span: 8 }}
      wrapperCol={{ span: 14 }}
      layout="horizontal"
      form={formController}
      initialValues={{
        type: SurveyType.OneTime,
        sendImmediately: true,
        count: 1,
        unit: SurveyIntervalUnit.Week,
        editable: false,
        sentTimeConfig: { mode: "absolute", avoidSendingOnTheNight: true },
        scheduleTiming: {
          anchor: ProgramAutomationScheduleAnchor.WeekDay,
        },
      }}
      onFinish={handleSubmit}
    >
      <Form.Item
        name="templateId"
        label={t<string>("form.selectSurvey")}
        rules={[{ required: true, message: t<string>("common:validationErrors.fieldIsRequired") }]}
      >
        <Select
          showSearch
          notFoundContent={t("form.notFoundSurvey")}
          options={createSurveysOptions(list)}
          placeholder={t("common:selectPlaceholder")}
          autoFocus
          loading={listStatus === RequestStatus.FETCHING}
        />
      </Form.Item>

      {templatedId ? (
        <>
          <Form.Item
            name="title"
            label={t<string>("form.title")}
            tooltip={t<string>("form.titleVisibleForClient")}
            rules={[{ required: true, message: t<string>("common:validationErrors.fieldIsRequired") }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="description" label={t<string>("form.desc")} tooltip={t<string>("form.descVisibleForClient")}>
            <Input.TextArea rows={2} />
          </Form.Item>

          <Form.Item
            name="editable"
            label={t<string>("form.editable")}
            valuePropName="checked"
            tooltip={t<string>("form.editableDesc")}
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="type"
            label={t<string>("form.surveyType")}
            rules={[{ required: true, message: t<string>("common:validationErrors.fieldIsRequired") }]}
          >
            <Select options={createSurveyTypeOptions(t)} placeholder={t("common:selectPlaceholder")} />
          </Form.Item>

          {fromAutomation && <FiltersField formController={formController as FiltersFieldProps["formController"]} />}
          {fromAutomation && <ScheduleTimingFields formController={formController} />}

          {surveyType === SurveyType.OneTime ? (
            <>
              {fromAutomation ? (
                <TimeField showOnlyAbsolute={false} name="timeOfDelivery" label={t<string>("form.timeOfDelivery")} />
              ) : (
                <Form.Item
                  name="timeOfDelivery"
                  label={t("form.timeOfDelivery")}
                  rules={[{ required: true, message: t<string>("common:validationErrors.fieldIsRequired") }]}
                >
                  <TimePicker
                    format="HH:mm"
                    minuteStep={15}
                    placeholder={t<string>("form.timeOfDeliveryPlaceholder")}
                    allowClear={false}
                    disabledTime={() => ({
                      disabledHours: () => DISABLED_HOURS,
                    })}
                  />
                </Form.Item>
              )}
            </>
          ) : (
            <>
              <Form.Item
                name="dayOfDelivery"
                label={t("form.dayOfDelivery")}
                rules={[{ required: true, message: t<string>("common:validationErrors.fieldIsRequired") }]}
              >
                <Radio.Group size="small" disabled>
                  {createScheduleOptions(t).map((item) => (
                    <Radio.Button key={item.value} value={item.value}>
                      {item.label}
                    </Radio.Button>
                  ))}
                </Radio.Group>
              </Form.Item>
              {fromAutomation ? (
                <TimeField showOnlyAbsolute={false} name="timeOfDelivery" label={t<string>("form.timeOfDelivery")} />
              ) : (
                <Form.Item
                  name="timeOfDelivery"
                  label={t("form.timeOfDelivery")}
                  rules={[{ required: true, message: t<string>("common:validationErrors.fieldIsRequired") }]}
                >
                  <TimePicker
                    format="HH:mm"
                    minuteStep={15}
                    placeholder={t<string>("form.timeOfDeliveryPlaceholder")}
                    allowClear={false}
                    disabledTime={() => ({
                      disabledHours: () => DISABLED_HOURS,
                    })}
                  />
                </Form.Item>
              )}

              <Form.Item label={t("form.surveyInterval")}>
                <Space.Compact>
                  <Form.Item
                    name="count"
                    noStyle
                    rules={[{ required: true, message: t<string>("common:validationErrors.fieldIsRequired") }]}
                  >
                    <InputNumber min={1} style={{ width: "30%" }} />
                  </Form.Item>

                  <Form.Item
                    name="unit"
                    noStyle
                    rules={[{ required: true, message: t<string>("common:validationErrors.fieldIsRequired") }]}
                  >
                    <Select
                      style={{ width: "52%" }}
                      placeholder={t("common:selectPlaceholder")}
                      options={createIntervalOptions(t, true)}
                    />
                  </Form.Item>
                </Space.Compact>
              </Form.Item>

              <Form.Item label={t("form.cycleType")} name="cycleType">
                <Select
                  style={{ width: "60%" }}
                  placeholder={t("common:selectPlaceholder")}
                  options={createCycleTypeOptions(t, false)}
                />
              </Form.Item>

              {renderCycleTypeField()}
            </>
          )}

          {variables && variables.length > 0 && (
            <div>
              <Divider />

              <Row>
                <Col
                  xs={{
                    span: 24,
                    offset: 0,
                  }}
                  sm={{
                    span: 10,
                    offset: 8,
                  }}
                >
                  <Typography.Title level={5}>Domyśle wartości dla automatyzacji (opcjonalne)</Typography.Title>
                </Col>
              </Row>

              {variables.map((variableName) => (
                <Form.Item key={variableName} label={variableName} name={["variables", variableName]}>
                  <Input />
                </Form.Item>
              ))}
            </div>
          )}
        </>
      ) : (
        <Empty description="Wybierz szablon ankiety z listy" />
      )}
    </Form>
  );
};

export default AddSurveyTemplateForm;
